import axios from "axios";

import { privateApiClient, publicApiClient } from "../utils/axios.utils";
import { API_URL, AUTH_ENDPOINTS, STORAGE_KEYS } from "../constants/api";

// Auth service interfaces
export interface LoginCredentials {
  email: string;
  device?: "web";
  password: string;
}

export interface RegisterCredentials {
  email: string;
  device?: "web";
  password: string;
  role: "user" | "school-admin";
}

export interface AuthTokens {
  accessToken: string;
}

export interface UserData {
  id?: string | number;
  email: string;
  role?: string;
  role_id?: number;
  last_name?: string;
  first_name?: string;
  phone?: string;
  password?: string;
  phone_number?: string;
  profile_image?: string;
  email_verified?: boolean;
  password_confirmation?: string;
}

export interface LoginResponse {
  status: string;
  message: string;
  data: AuthTokens;
}

export interface UpdateProfileData {
  email?: string;
  last_name?: string;
  first_name?: string;
  phone_number?: string;
  profile_image?: string;
}

// Auth service methods
const AuthService = {
  /**
   * Register new user
   */
  register: async (
    credentials: RegisterCredentials
  ): Promise<{ message: string }> => {
    const response = await publicApiClient.post(AUTH_ENDPOINTS.REGISTER, {
      ...credentials,
      device: "web",
    });

    return response.data.data;
  },

  /**
   * Verify email with OTP
   */
  verifyEmail: async (
    email: string,
    otp: string,
    isReset?: boolean
  ): Promise<{ message: string }> => {
    const response = await publicApiClient.post(AUTH_ENDPOINTS.VERIFY_EMAIL, {
      email,
      otp,
      isReset: isReset || false,
    });

    return response.data.data;
  },

  /**
   * Login user with email and password
   */
  login: async (
    credentials: LoginCredentials
  ): Promise<{
    accessToken: string;
    user: UserData;
  }> => {
    const response = await publicApiClient.post(
      AUTH_ENDPOINTS.LOGIN,
      {
        ...credentials,
        device: "web",
      },
      {
        withCredentials: true, // Ensure cookies are sent and received
      }
    );

    const { accessToken, user } = response.data.data;

    // Save tokens to storage
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);

    return { accessToken, user };
  },

  /**
   * Request OTP for verification
   */
  requestOtp: async (email: string): Promise<{ message: string }> => {
    const response = await publicApiClient.post(AUTH_ENDPOINTS.REQUEST_OTP, {
      email,
      device: "web",
    });

    return response.data.data;
  },

  /**
   * Forgot password - request password reset
   */
  forgotPassword: async (email: string): Promise<{ message: string }> => {
    const response = await publicApiClient.post(
      AUTH_ENDPOINTS.FORGOT_PASSWORD,
      {
        email,
        device: "web",
      }
    );

    return response.data.data;
  },

  /**
   * Reset password with new password
   */
  resetPassword: async (
    token: string,
    password: string,
    password_confirmation: string
  ): Promise<{ message: string }> => {
    const response = await publicApiClient.post(AUTH_ENDPOINTS.RESET_PASSWORD, {
      token,
      password,
      password_confirmation,
    });

    return response.data.data;
  },

  /**
   * Refresh authentication token
   */
  refreshToken: async (): Promise<{
    accessToken: string;
  }> => {
    try {
      // send the request with browser cookies (refresh token is in httpOnly cookie)
      const response = await publicApiClient.post(
        AUTH_ENDPOINTS.REFRESH_TOKEN,
        {}, // Empty body - refresh token comes from cookie
        {
          withCredentials: true, // Ensure cookies are sent
        }
      );

      const { accessToken } = response.data.data;

      if (!accessToken) {
        throw new Error("Invalid refresh token response: no accessToken");
      }

      // Save new token
      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);

      return { accessToken };
    } catch (error) {
      // Clear tokens on refresh error
      localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
      throw error;
    }
  },

  /**
   * Revoke refresh token
   */
  revokeToken: async (): Promise<{ message: string }> => {
    const response = await publicApiClient.post(AUTH_ENDPOINTS.REVOKE_TOKEN);

    return response.data.data;
  },

  /**
   * Logout user
   */
  logout: async (): Promise<{ message: string }> => {
    try {
      // First get the tokens we need for the logout call
      const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);

      // Clear tokens immediately to prevent auto refresh attempts
      localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);

      // Only call the API if we have valid tokens
      if (accessToken) {
        try {
          // Use axios directly to avoid interceptors
          await axios.post(
            `${API_URL}${AUTH_ENDPOINTS.LOGOUT}`,
            {
              device: "web",
            },
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json",
              },
              withCredentials: true,
            }
          );
        } catch (apiError) {
          // Ignore API errors during logout
          console.log("Logout API error (ignored):", apiError);
        }
      }

      return { message: "Logged out successfully" };
    } catch (error) {
      console.error("Logout error:", error);
      // We've already cleared tokens, so just return success
      return { message: "Logged out successfully" };
    }
  },

  /**
   * Logout all sessions for current user
   */
  logoutAll: async (): Promise<{ message: string }> => {
    try {
      const response = await privateApiClient.post(AUTH_ENDPOINTS.LOGOUT_ALL);

      // Clear tokens
      localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);

      return response.data.data;
    } catch (error) {
      // Clear tokens on error for safety
      localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
      throw error;
    }
  },

  /**
   * Check if user is authenticated (has valid tokens)
   */
  isAuthenticated: (): boolean => {
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    return !!token;
  },

  /**
   * Get current user information
   */
  getCurrentUser: async (): Promise<UserData> => {
    const response = await privateApiClient.get(AUTH_ENDPOINTS.ME);
    return response.data.data;
  },
};

export default AuthService;
